import { apiFetch } from '../authentication/authUtils';
import { getResponseErrorTitle } from '../shared/apiUtils';

export type IdpType = 'OAUTH' | 'SAML';

export interface IdentityProvider {
    id: string;
    accountId: string;
    name: string;
    type: IdpType;
    allowedAudience: string;
    createdAt: string;
    updatedAt: string;
}

export interface PaginatedResultsResponse<T> {
    total: number;
    pageSize: number;
    page: number;
    results: T[];
}

export interface CreateOAuthIdentityProvider {
    type: 'OAUTH';
    name: string;
    allowedAudience: string;
    clientId: string;
    clientSecret: string;
    wellKnownUrl: string;
}

export interface CreateSamlIdentityProvider {
    type: 'SAML';
    name: string;
    allowedAudience: string;
    entityId: string;
    metadata: string;
}

export type CreateIdentityProvider = CreateOAuthIdentityProvider | CreateSamlIdentityProvider;

export interface OAuthIdentityProviderDetail extends IdentityProvider {
    type: 'OAUTH';
    clientId: string;
    clientSecret: string;
    wellKnownUrl: string;
}

export interface SamlIdentityProviderDetail extends IdentityProvider {
    type: 'SAML';
    entityId: string;
    metadata: string;
}

export type IdentityProviderDetail = OAuthIdentityProviderDetail | SamlIdentityProviderDetail;

export async function fetchIdentityProviders(
    page = 1,
    pageSize = 20,
): Promise<PaginatedResultsResponse<IdentityProvider>> {
    const response = await apiFetch(`/identityprovider?page=${page}&pageSize=${pageSize}`);

    if (!response.ok) {
        throw new Error(await getResponseErrorTitle(response));
    }

    return response.json() as Promise<PaginatedResultsResponse<IdentityProvider>>;
}

export async function createIdentityProvider(
    payload: CreateIdentityProvider,
): Promise<IdentityProvider> {
    const response = await apiFetch('/identityprovider', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });

    if (!response.ok) {
        throw new Error(await getResponseErrorTitle(response));
    }

    return response.json() as Promise<IdentityProvider>;
}

export async function deleteIdentityProvider(id: string): Promise<void> {
    const response = await apiFetch(`/identityprovider/${id}`, { method: 'DELETE' });

    if (!response.ok) {
        throw new Error(await getResponseErrorTitle(response));
    }
}

export async function fetchIdentityProviderById(id: string): Promise<IdentityProviderDetail> {
    const response = await apiFetch(`/identityprovider/${id}`);

    if (!response.ok) {
        throw new Error(await getResponseErrorTitle(response));
    }

    return response.json() as Promise<IdentityProviderDetail>;
}

export async function updateIdentityProvider(
    id: string,
    payload: CreateIdentityProvider,
): Promise<IdentityProvider> {
    const response = await apiFetch(`/identityprovider/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });

    if (!response.ok) {
        throw new Error(await getResponseErrorTitle(response));
    }

    return response.json() as Promise<IdentityProvider>;
}
